<template lang="pug">
.fill.opacity-40.bg-gradient-to-b.bg-bottom.-z-1(
  class="from-gray-800/40 via-gray-800/50 to-gray-800/80"
  data-course-title-fill=""
)
.screen-esg-title.wrapper.items-center(ref="elRef")
  .flex.flex-col.flex-1.justify-center.z-10.self-stretch.break-words.items-center
    .logo-wrapper.mx-auto(
      :class="{ 'max-w-[20em] max-h-[20em]': !showVinciLogo }"
    )
      uc-logo#course-title-logo.course-title-logo.w-auto.inline-block.object-contain(
        :class="[showVinciLogo ? 'h-[2.5em]' : 'h-full min-h-[5em]']"
      )

    //- animated course headings
    uc-text#course-title-abbr.course-title(
      :text="'ESG'"
      class="[letterSpacing:0.2em] pl-[0.2em]"
      k="course.title.abbr"
      type="h1"
      uc-word-skip=""
    )
    uc-text#course-title.course-subtitle.border-white.py-4.px-8(
      :text="content.course.title"
      k="course.title"
      type="p"
      uc-word-skip=""
    )
    uc-text#course-subtitle.course-subtitle.border-t-2.border-white.py-4.px-8(
      :text="content.course.subtitle"
      k="course.subtitle"
      type="p"
      uc-word-skip=""
    )

    //- word doc headings
    uc-text.course-title(
      :text="content.course.title"
      k="course.title"
      type="h1"
      uc-word-only=""
    )
    uc-text.course-subtitle(
      :text="content.course.subtitle"
      k="course.subtitle"
      type="h2"
      uc-word-only=""
      v-if="content.course.subtitle"
    )

    #scroll-down(
      uc-word-skip=""
      v-if="(showStartButton && !bookmarkAvailable && !audioDoc) || (showStartButton && editModeAvailable)"
    )
      uc-button.mt-20.btn.btn-primary.btn-lg.unlock.ripple
        uc-text(text="Start")

  .scroll-down-wrapper(
    uc-word-skip=""
    v-if="!(showStartButton && !bookmarkAvailable) && !audioDoc"
  )
    #scroll-down.text-center.my-12
      .prose-stack.mx-auto.p-8.rounded-sm.bg-slate-200.flex.flex-col.gap-2(
        class="dark_bg-gray-900/60"
        v-if="(passed || failed) && !editModeAvailable"
      )
        uc-text.text-center.text-xl(
          text="You have completed this course."
          v-if="passed"
        )
        uc-text.text-center.text-xl(
          text="You have failed this course."
          v-else-if="failed"
        )
        uc-text.text-center.text-xl(
          text="You are welcome to review it."
        )
        uc-button.btn.btn-primary.unlock.mt-6
          uc-text.text-xl(text="Review")
      
      .course-title-resume.prose-stack.mx-auto.p-8.rounded-sm.text-slate-900.dark_text-slate-200(
        class="dark_bg-gray-900/60 bg-slate-100/50"
        v-else-if="bookmarkAvailable && !editModeAvailable"
      )
        uc-text.text-center.text-xl(
          text="You have saved progress in this course."
        )
        p.mt-8
          uc-button.btn.btn-primary(@click="goToBookmark")
            uc-text.text-xl(text="Resume")

      div(v-else-if="!showStartButton")
        button.unlock.inline-block.text-primary-400.p-2.focus-visible_ring(
          @click="scrollDown"
        )
          .text-center.inline-block.text-6xl.animate-bounce.mb-4
            uc-icon(icon="theme:down")
          .text-center.text-lg.font-semibold.title-scroll-text
            uc-text(text="Scroll to continue")
</template>

<script lang="ts">
export default {
  inheritAttrs: false
};
</script>

<script setup lang="ts">
import gsap from 'gsap';
import SplitText from 'gsap/SplitText';
import { throttle } from 'lodash-es';
import { inject, onMounted, ref, unref, computed, toRef } from 'vue';
import useContent from '../stores/useContent';
import { storeToRefs } from 'pinia';
import useEditMode from '../stores/useEditMode';
import useScreenAudio from '../lib/useScreenAudio';
import esgTitleSchema from '../lib/screen-schemas/screen-esg-title.schema';
import isHidden from '../lib/isHidden';
import { getLessonStatus } from '../lib/SCORM';

const { compiled } = storeToRefs(useContent());
const logo = computed(() => compiled.value.course.logo);
const passed = computed(() => getLessonStatus() === 'passed');
const failed = computed(() => getLessonStatus() === 'failed');
const props = defineProps({ screen: { required: true, type: Object } });
const { playSlot } = useScreenAudio(toRef(props, 'screen'), esgTitleSchema);
const { bookmark } = inject('data') as Data;
const goToBookmark = inject('goToBookmark');
const hasAudio = inject('hasAudio');
const audioDoc = inject('audioDoc');
const wordDoc = inject('wordDoc') as Ref<boolean>;
const moreButtons = inject('moreButtons');
const content = useContent().compiled;
const elRef = ref<HTMLElement>();
const { available: editModeAvailable, mode } = storeToRefs(useEditMode());

const showStartButton = computed(
  () => hasAudio.value || props.screen.start || moreButtons.value
);

const showVinciLogo = computed(
  () => logo.value === true || logo.value === undefined
);

const bookmarkAvailable = bookmark && !isHidden(bookmark);

gsap.registerPlugin(SplitText);

function scrollDown() {
  const el = unref(elRef);
  const page = el?.closest('.page');
  page.unlockAndScroll(props.screen.id);
}

onMounted(async () => {
  const el = unref(elRef);

  let onMove = (e: MouseEvent) => {
    const pctX = e.clientX / window.innerWidth;
    const pctY = e.clientY / window.innerHeight;

    gsap.to('[data-page-parallax="welcome"] .parallax-bg', {
      xPercent: 2 - 4 * pctX,
      rotateY: -1 + 2 * pctX,
      duration: 12,
      ease: 'power3'
    });

    gsap.to('[data-page-parallax="welcome"] .parallax-bg', {
      yPercent: -2 * pctY,
      scaleY: 1 - 0.02 * pctY,
      rotateX: 5 * pctY,
      duration: 6,
      ease: 'power4'
    });
  };

  el.addEventListener('mousemove', throttle(onMove, 200, { leading: true }));

  function playAudio() {
    if (wordDoc.value) return;

    const course = compiled.value.course;
    const text =
      [course?.title, course?.subtitle].filter(Boolean).join('. ') || undefined;
    playSlot('intro', text);
  }

  const split1 = new SplitText('#course-title-abbr');
  const split2 = new SplitText('#course-subtitle');

  const tl = gsap
    .timeline({
      paused: true,
      onComplete: () => {
        window.animatedTitle = true;
      }
    })
    .fromTo(
      '[data-course-title-fill]',
      { opacity: 0 },
      {
        duration: 1,
        opacity: 1,
        ease: 'linear'
      },
      0
    )
    .set('#course-title-logo', { scale: 1.5, transformOrigin: 'center' }, 0.5)
    .fromTo(
      '#course-title-logo',
      { autoAlpha: 0 },
      { autoAlpha: 1, duration: 1, ease: 'linear' },
      '>0.2'
    )
    .fromTo('#course-title-logo', { y: 200 }, { y: 0, duration: 1 }, '<')
    .to('#course-title-logo', { scale: 1, duration: 1 }, '>')
    .fromTo(
      split1.chars,
      { opacity: 0 },
      { opacity: 1, duration: 1, ease: 'linear', stagger: 0.4 },
      '>'
    )
    .fromTo(
      '#course-title',
      { opacity: 0 },
      { opacity: 1, duration: 0.5, ease: 'linear' }
    )
    .fromTo(
      '#course-subtitle',
      { opacity: 0 },
      { opacity: 1, duration: 0.5, ease: 'linear' },
      '<'
    )
    .fromTo(
      split2.words,
      { opacity: 0 },
      { opacity: 1, duration: 1, ease: 'linear', stagger: 0.4 },
      '>'
    )
    .add('afterTitles', '<')
    .call(playAudio, undefined, window.animatedTitle ? '>0.1' : '<');

  if (!window.animatedTitle) {
    tl.fromTo(
      '#scroll-down, .nav-buttons-container button',
      { opacity: 0 },
      { opacity: 1, duration: 1.0, ease: 'linear', stagger: 0.25 },
      '<'
    )
      .fromTo(
        '.nav-buttons-container button',
        { x: -100 },
        { x: 0, duration: 1.0, ease: 'elastic.out(1.5,1)', stagger: 0.25 },
        '<'
      )
      .fromTo(
        '.uc-language-widget',
        { opacity: 0 },
        { opacity: 1, duration: 1.0, ease: 'linear' },
        '<'
      )
      .from('.uc-language-widget', { yPercent: 100, duration: 1.0 }, '<');
  }

  tl.fromTo(
    '.uc-edit-bar',
    { opacity: 0, yPercent: -100 },
    { opacity: 1, yPercent: 0, duration: 1 },
    // FIXME restore the delay
    // FIXME is course-title required in every course?
    0
    // '>'
  );

  gsap
    .timeline({
      scrollTrigger: {
        scroller: el.closest('.scroller'),
        trigger: el,
        scrub: true,
        start: '100% 80%',
        end: '100% 20%'
      }
    })
    .to('[data-page-background="welcome"]', {
      blur: 10,
      ease: 'linear'
    })
    .fromTo('.bottom-left-logo', { autoAlpha: 0 }, { autoAlpha: 1 });

  if (window.animatedTitle) {
    tl.seek('afterTitles');
  }

  tl.play();
  window.animatedTitle = true;
});
</script>

<style>
.screen-esg-title.wrapper {
  min-height: calc(100vh - 4rem);
}
</style>
